<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <div v-if="success" class="card success-card">
          <div class="card-body green">You successfully edited a company!</div>
        </div>
      </b-col>
      <b-col sm="12">
        <b-card>
          <div slot="header">
            <strong>Edit Company</strong>
          </div>
          <b-form @submit.prevent="editCompany">
          <b-form-group>
            <label for="company">Company</label>
            <b-form-input
              type="text"
              v-validate="'required'"
              v-model="name"
              name="company_name"
              placeholder="Enter your company name"
              :class="{'has-error': errors.has('company_name')}"
            >
            </b-form-input>
            <span class="red">{{ errors.first('company_name') }}</span>
          </b-form-group>
          <b-row>
            <b-col sm="12">
              <b-form-group>
                <label class="typo__label">Assign Admins</label>
                <multiselect
                  v-model="selectedAdmins"
                  :options="admins"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :hide-selected="true"
                  :preserve-search="true"
                  placeholder=""
                  label="name"
                  track-by="name"
                  :preselect-first="true">
                  <template slot="tag" slot-scope="props">
                    <span class="custom__tag">
                      <span>{{ props.option.name }} ({{ props.option.email }})</span>
                      <span v-if="admin.admin_id !== props.option.id" class="custom__remove" @click="props.remove(props.option)">X</span>
                      <span v-else class="custom__remove__disabled" v-tooltip.top-center="msgRemove">X</span>
                    </span>
                  </template>
                </multiselect>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6">
              <b-form-group>
                <label for="street">Street</label>
                <b-form-input
                  type="text"
                  v-validate="'required'"
                  v-model="streetAddress"
                  placeholder="Enter street name"
                  name="street_address"
                  :class="{'has-error': errors.has('street_address')}"
                ></b-form-input>
                <span class="red">{{ errors.first('street_address') }}</span>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <label for="city">City</label>
                <b-form-input
                  type="text"
                  v-validate="'required'"
                  v-model="city"
                  placeholder="Enter your city"
                  name="city"
                  :class="{'has-error': errors.has('city')}"
                >
                </b-form-input>
                <span class="red">{{ errors.first('city') }}</span>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6">
              <b-form-group
                label="Province / State"
                label-for="selectProvinceState">
                <b-form-select id="selectProvinceState"
                  v-model="province"
                  v-validate="'required'"
                  :plain="true"
                  :options="stateProvince"
                  value="Please select"
                  name="state_province"
                  :class="{'has-error': errors.has('state_province')}"
                >
                </b-form-select>
                <span class="red">{{ errors.first('state_province') }}</span>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <label for="postal-code">Postal / Zip Code</label>
                <b-form-input
                  type="text"
                  v-validate="'required'"
                  v-model="postalCode"
                  placeholder="Postal / Zip Code"
                  name="postal_code"
                  :class="{'has-error': errors.has('postal_code')}"
                >
                </b-form-input>
                <span class="red">{{ errors.first('postal_code') }}</span>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="form-actions">
            <b-button type="submit" name="submit" variant="primary">Edit</b-button>
          </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import getAdmin from './../../mixins/getAdmin'
import Multiselect from 'vue-multiselect'
import stateProvince from '../../stateProvince'

export default {
  name: 'EditCompany',
  mixins: [
    getAdmin
  ],
  components: { Multiselect },
  data () {
    return {
      admin: {},
      admins: [],
      city: '',
      msgRemove: 'You cannot remove yourself',
      name: '',
      options: [],
      originalAdmins: [],
      postalCode: '',
      province: '',
      selectedAdmins: [],
      streetAddress: '',
      success: false,
      stateProvince
    }
  },
  methods: {
    /**
     * Assign an admin to a company by giving them full read permissions of the data. Note: this does not necessarily mean they have access to edit or delete the company. Only if they have level 1 access can they edit and delete.
     * @param {integer} companyId - The id of the company for the admin to be assigned to.
     * @param {integer} adminId - The id of the admin to be assigned for.
     */
    assignAdmin: function (companyId, adminId) {
      let self = this

      axios.post(`${process.env.VUE_APP_API_URL}api/admins/assign`, {
        admin_id: adminId,
        company_id: companyId
      })
        .then(function (response) {
          self.success = true
          window.scrollTo(0, 0)
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Submits the form information to the backend API once submit button is pressed. The information is passed through a validator to make sure they meet the required details. Once the data is submitted, the admins that were selected to be assigned to this company, and who weren't already in the array are sent to the assignAdmin method to be processed. The admins who were in the original array and who were removed from the selection are sent to the removeAdmin method to have their permissions revoked.
     */
    editCompany: function () {
      let self = this

      const { name, streetAddress, city, postalCode, province } = this

      this.$validator.validateAll().then((result) => {
        if (result) {
          axios.put(`${process.env.VUE_APP_API_URL}api/companies/${this.$route.params.id}`, {
            name,
            street_address: streetAddress,
            city,
            postal_code: postalCode,
            province
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-access-token': localStorage.getItem('user-token')
            }
          })
            .then(function (response) {
              let adminId = 0

              for (let i = 0; i < self.admins.length; i++) {
                adminId = self.admins[i].id

                if (self.inAdminsArray(self.originalAdmins, adminId) && !self.inAdminsArray(self.selectedAdmins, adminId)) {
                  self.removeAdmin(response.data.company_id, adminId)
                } else if (!self.inAdminsArray(self.originalAdmins, adminId) && self.inAdminsArray(self.selectedAdmins, adminId)) {
                  self.assignAdmin(response.data.company_id, adminId)
                }
              }

              self.success = true
            })
            .catch(function (error) {
              console.log(error)
            })
        }
      }).catch(() => {
        console.log(this.errors.all())
      })
    },
    /**
     * Gets the existing company details in order to auto-populate the edit form.
     */
    getCompanyDetails: function () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/companies/${this.$route.params.id}${(this.admin.level === 0 ? '/all' : '')}`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          let data = response.data.data

          self.name = data.name
          self.streetAddress = data.street_address
          self.city = data.city
          self.postalCode = data.postal_code
          self.province = data.province
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Formats the list of organization admins into a array of objects to rendered in a bootstrap-vue table.
     * @param {array} data - The array of admins in the organization.
     * @return {array} The formatted object array of admins.
     */
    getAdmins (data) {
      let newData = []

      for (let i = 0; i < data.length; i++) {
        newData.push({
          id: data[i].admin_id,
          name: data[i].first_name + ' ' + data[i].last_name,
          email: data[i].email,
          level: data[i].level
        })
      }

      return newData
    },
    /**
     * Gets the list of admins associated with an organization from the backend API and stores it in the admins array data property.
     */
    getOrganizationAdmins () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/admins`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          self.admins = self.getAdmins(response.data.data)
          self.totalRows = self.admins.length
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Gets the list of admins that have access permissions to this company and stores the data in both the selectedAdmins array, and the originalAdmins array.
     */
    getCompanyPermissions () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/permissions/by_company/${this.$route.params.id}`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          let data = response.data.data

          self.selectedAdmins = self.getAdmins(data)
          self.originalAdmins = self.getAdmins(data)
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Determines whether or not the given admin id exists in the admins array of objects.
     * @param {array} adminsArray - An array of admin objects.
     * @param {integer} adminId - The id of the admin to look for.
     * @return {boolean} Whether or not the id exists.
     */
    inAdminsArray (adminsArray, adminId) {
      for (let i = 0; i < adminsArray.length; i++) {
        if (adminId === adminsArray[i].id) {
          return true
        }
      }

      return false
    },
    /**
     * Remove an admins permissions for a company from the backend API.
     * @param {integer} companyId - The id of the company for the admin to be removed from.
     * @param {integer} adminId - The id of the admin to be removed.
     */
    removeAdmin (companyId, adminId) {
      let self = this

      axios.post(`${process.env.VUE_APP_API_URL}api/permissions/remove_admin`, {
        admin_id: adminId,
        company_id: companyId
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          self.success = true
        })
        .catch(function (error) {
          console.log(error)
        })
    }
  },
  async mounted () {
    await this.getAdmin()
    this.getOrganizationAdmins()
    this.getCompanyDetails()
    this.getCompanyPermissions()
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
